<template>
  <div>
    <div class="kt-login-v2__title">
      <h3>Aanmelden bij account</h3>
    </div>

    <form
      id="kt_login_form"
      class="kt-login-v2__form kt-form"
      @submit.prevent
    >
      <maintenance-notification />

      <div
        v-if="!browserSupported"
        class="alert alert-bold alert-solid-danger alert-dismissible"
        role="alert"
      >
        <div class="alert-text">
          Mertis werkt alleen in combinatie met Google Chrome.
        </div>
      </div>

      <alert-message-container
        v-if="requestMessage"
        :message="requestMessage"
        :warning="false"
        :classes="hasValidationError ? 'alert-danger' : 'alert-info'"
      />

      <div class="form-group">
        <input-text
          id="email"
          v-model="email"
          type="email"
          name="email"
          title="e-mailadres"
          class="form-control height-unset pl-3"
          autocomplete="none"
          placeholder="E-mailadres"
        />
      </div>

      <div class="form-group">
        <div class="kt-input-icon pull-right">
          <input-text
            id="password"
            v-model="password"
            :type="showPassword ? 'text' : 'password'"
            name="password"
            class="form-control height-unset pl-3"
            placeholder="Wachtwoord"
            autocomplete="none"
          />

          <span
            class="kt-input-icon__icon kt-input-icon__icon--right"
            @click="togglePassword"
          >
            <span>
              <i
                id="togglePassword"
                :class="showPassword ? 'fas fa-eye' : 'fas fa-eye-slash'"
              />
            </span>
          </span>
        </div>
      </div>

      <div class="kt-login-v2__actions mt-2">
        <a
          class="kt-link kt-login-v2__link cursor-pointer"
          tabindex="0"
          @click="page = AuthPage.FORGET_PASSWORD;"
        >
          Wachtwoord vergeten?
        </a>

        <button
          id="kt_login_submit"
          type="submit"
          class="btn btn--primary"
          @click="submitForm"
        >
          Inloggen
        </button>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { onBeforeMount, ref } from 'vue'
import { storeToRefs } from 'pinia'
import type { AuthRequest } from '@/stores/auth'
import { AuthPage, useAuthStore } from '@/stores/auth'
import InputText from '@/components/input/InputText.vue'
import AlertMessageContainer from '@/components/AlertMessageContainer.vue'
import MaintenanceNotification from '@/auth/components/MaintenanceNotification.vue'

const authStore = useAuthStore()

const {
  // state
  email,
  password,
  page,
  showPassword,
  hasValidationError,
  requestMessage,
  // actions
  togglePassword,
} = storeToRefs(authStore)

const browserSupported = ref<boolean>(true)

onBeforeMount(() => {
  if (!navigator.userAgentData?.brands?.some(b => b.brand === 'Google Chrome')) {
    browserSupported.value = false
  }
})

const submitForm = async () => {
  if (!email.value || !password.value) return

  return await authStore.login()
    .then((response: AuthRequest) => {
      // User has two factor authentication enabled
      if (response.data.two_factor) {
        page.value = AuthPage.TWO_FACTOR_AUTH
        return
      }

      // redirects to the desired page when user is authenticated
      location.reload()
    })
    .catch((e) => {
      if (e?.response?.status === 422) {
        hasValidationError.value = true

        requestMessage.value = e.response.data.message ?? 'Er is iets misgegaan tijdens het inloggen.'
        return
      }

      console.error(e)
    })
}
</script>

<style scoped lang="scss">
#togglePassword {
  font-size: 1.15em !important;
}

.height-unset {
  height: unset;
}

.kt-link:after {
  transition: none !important;
}

.kt-input-icon__icon {
  pointer-events: unset !important;
}

#kt_login_form i {
  cursor: pointer;
}
</style>
